import { type DateLike, DatetimeFormatType } from '@tundr/i18n';
import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
import type { I18nTFunction } from '@tundr/validation';
import { endOfDay, startOfDay } from 'date-fns';
import type {
  ActiveFilter,
  ActiveFilterModelValue,
  DateFilterModelValue,
  DateRangeFilterModelValue,
  DateRangeFilterProps,
  Filter,
  FiltersForTable,
  MultipleSelectModelValue,
  SingleDateFilterProps,
  SingleSelectModelValue,
  TypedTextModelValue,
} from '../../store';

export enum FilterTypes {
  TEXT_SEARCH = 'needle',
  DATE = 'date',
  DATE_RANGE = 'dateRange',
  SELECT = 'select',
  SELECT_MULTIPLE = 'selectMultiple',
  SELECT_PAGINATED = 'selectPaginated',
  TYPED_TEXT = 'typedText',
}

const MAX_COMPACT_FILTERS = 3;

/*
  Up to three configurable filters the top bar renders them inline instead of
  behind the filter selector: they must stay mounted, so clearing sets their
  value to undefined rather than removing them from the namespace.

  `compact` overrides that count in both directions, for the tables whose
  filter bar should not follow it: pass `false` to always use the filter
  selector, `true` to keep more than three filters inline.
*/
export const hasCompactFilters = (
  filtersConfig?: ActiveFilter[],
  compact?: boolean,
): boolean => {
  const configurableFilters = (filtersConfig ?? []).filter(
    (filter) => filter.filterType !== FilterTypes.TEXT_SEARCH,
  );

  if (configurableFilters.length === 0) {
    return false;
  }

  return compact ?? configurableFilters.length <= MAX_COMPACT_FILTERS;
};

export const getFilterTypeIconByType = (type: FilterTypes) => {
  switch (type) {
    case FilterTypes.DATE:
      return TUNDR_ICONS.CALENDAR;
    case FilterTypes.DATE_RANGE:
      return TUNDR_ICONS.CALENDAR;
    case FilterTypes.SELECT:
      return TUNDR_ICONS.LIST;
    case FilterTypes.SELECT_PAGINATED:
      return TUNDR_ICONS.LIST;
    case FilterTypes.SELECT_MULTIPLE:
      return TUNDR_ICONS.CHECK;
    case FilterTypes.TYPED_TEXT:
      return TUNDR_ICONS.PEN;
  }
};

type getOptionsFromEnumProps = {
  enum: Record<string, string>;
  translationKey: string;
  t: I18nTFunction;
  exclude?: string[];
};

export function getSelectOptionKey(value: string | string[]): string;
export function getSelectOptionKey(
  value: string | string[] | undefined,
): string | undefined;
export function getSelectOptionKey(
  value: string | string[] | undefined,
): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value;
}

export const getOptionsFromEnum = ({
  enum: enumType,
  translationKey,
  t,
  exclude,
}: getOptionsFromEnumProps) => {
  return Object.keys(enumType)
    .filter((key) => !exclude?.includes(key))
    .map((key) => ({
      label: t(`${translationKey}.${enumType[key]}`),
      value: key,
    }));
};

export const parseModelFromFilterType = (
  type: FilterTypes,
  modelValue: ActiveFilterModelValue,
  d: (date: DateLike, formatType: DatetimeFormatType) => string,
) => {
  switch (type) {
    case FilterTypes.DATE:
      return d(
        modelValue as DateFilterModelValue,
        DatetimeFormatType.SHORT_DATE,
      );
    case FilterTypes.DATE_RANGE: {
      const { startDate, endDate } = modelValue as DateRangeFilterModelValue;

      if (startDate && endDate) {
        return `${d(startDate, DatetimeFormatType.SHORT_DATE)} - ${d(endDate, DatetimeFormatType.SHORT_DATE)}`;
      }
      if (startDate) {
        return `${d(startDate, DatetimeFormatType.SHORT_DATE)}`;
      }
      if (endDate) {
        return `${d(endDate, DatetimeFormatType.SHORT_DATE)}`;
      }
      break;
    }
    case FilterTypes.SELECT:
    case FilterTypes.SELECT_PAGINATED:
      return (
        (modelValue as SingleSelectModelValue).label ||
        getSelectOptionKey((modelValue as SingleSelectModelValue).value)
      );
    case FilterTypes.SELECT_MULTIPLE:
      return (modelValue as MultipleSelectModelValue)
        .map((m) => m.label || getSelectOptionKey(m.value))
        .join(', ');
    case FilterTypes.TEXT_SEARCH:
    case FilterTypes.TYPED_TEXT:
      return String(modelValue as TypedTextModelValue);
  }
};

export const transformFiltersToPayload = (
  payload: Record<string, unknown> = {},
  filters?: FiltersForTable,
  excludeNeedle?: boolean,
): Record<string, unknown> => {
  const res: Record<string, unknown> = { ...payload };
  if (filters) {
    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      if (filter) {
        switch (filter.filterType) {
          case FilterTypes.TEXT_SEARCH:
            if (!excludeNeedle && filter.value) {
              res[key] = filter.value as string;
              // Add needleBy if present for column-specific search
              if (filter.needleBy) {
                res.needleBy = filter.needleBy;
              }
            }
            break;
          case FilterTypes.DATE_RANGE: {
            filter.value = filter.value as DateRangeFilterModelValue;
            if (!filter.value) {
              break;
            }
            const resFromFieldName = filter.value.startDate
              ? (filter.fromKey ?? 'from')
              : undefined;

            const resToFieldName = filter.value.endDate
              ? (filter.toKey ?? 'to')
              : undefined;

            if (resFromFieldName)
              res[resFromFieldName] = (
                filter.value as DateRangeFilterModelValue
              ).startDate
                ? startOfDay(
                    new Date(
                      (filter.value as DateRangeFilterModelValue)
                        .startDate as Date,
                    ),
                  ).toISOString()
                : '';
            if (resToFieldName)
              res[resToFieldName] = (filter.value as DateRangeFilterModelValue)
                .endDate
                ? endOfDay(
                    new Date(
                      (filter.value as DateRangeFilterModelValue)
                        .endDate as Date,
                    ),
                  ).toISOString()
                : '';
            break;
          }

          case FilterTypes.DATE:
            res[key] = (filter.value as DateFilterModelValue)
              ? startOfDay(
                  new Date(filter.value as DateFilterModelValue),
                ).toISOString()
              : '';
            break;
          case FilterTypes.SELECT:
          case FilterTypes.SELECT_PAGINATED:
            res[key] =
              (filter.value as SingleSelectModelValue) &&
              (filter.value as SingleSelectModelValue).value;
            break;
          case FilterTypes.SELECT_MULTIPLE:
            res[key] = ((filter.value as MultipleSelectModelValue) || [])
              .map((m) => m.value)
              .join(',');
            break;
          case FilterTypes.TYPED_TEXT:
            res[key] = filter.value as string;
            break;
        }
      }
    });
  }
  return { ...res, ...payload };
};

export function reviveDates(
  filterObject: Record<string, Filter[]>,
): Record<string, Filter[]> {
  const parseDate = (date: unknown): Date | undefined => {
    if (date instanceof Date) {
      return Number.isNaN(date.getTime()) ? undefined : date;
    }
    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  };

  const reviveFilter = (filter: Filter): Filter => {
    if (
      filter &&
      filter.filterType === FilterTypes.DATE &&
      (filter as SingleDateFilterProps).modelValue
    ) {
      const dateFilter = filter as SingleDateFilterProps;
      return {
        ...filter,
        modelValue: parseDate(dateFilter.modelValue),
        ...(dateFilter.max === undefined
          ? {}
          : { max: parseDate(dateFilter.max) }),
        ...(dateFilter.min === undefined
          ? {}
          : { min: parseDate(dateFilter.min) }),
      } as Filter;
    }
    if (
      filter.filterType === FilterTypes.DATE_RANGE &&
      (filter as DateRangeFilterProps).modelValue?.startDate &&
      (filter as DateRangeFilterProps).modelValue?.endDate
    ) {
      const rangeFilter = filter as DateRangeFilterProps;
      const reviveBounds = (bounds: DateRangeFilterProps['startDate']) =>
        bounds === undefined
          ? undefined
          : { max: parseDate(bounds.max), min: parseDate(bounds.min) };

      return {
        ...filter,
        ...(rangeFilter.startDate === undefined
          ? {}
          : { startDate: reviveBounds(rangeFilter.startDate) }),
        ...(rangeFilter.endDate === undefined
          ? {}
          : { endDate: reviveBounds(rangeFilter.endDate) }),
        modelValue: {
          startDate: parseDate(rangeFilter.modelValue!.startDate),
          endDate: parseDate(rangeFilter.modelValue!.endDate),
        },
      } as Filter;
    }
    return filter;
  };

  return Object.fromEntries(
    Object.entries(filterObject).map(([key, filters]) => [
      key,
      filters.map(reviveFilter),
    ]),
  );
}
